
  import { Component, Vue, Emit, Watch } from "vue-property-decorator";
  import { FeatureCollection } from "features/typescript/MapShapes";
  import { convertToDecimal, normalizePolygonCoords, validateCoordinate } from "../../../core/utils/map-utils";
  import { validationMessages } from '../../validationsMessages';
import { EventBus, TypeEventBus } from "../../../core/utils";
  
  interface CoordinateRow {
    latitude: { direction: string; degrees: number; minutes: number; seconds: number; };
    longitude: { direction: string; degrees: number; minutes: number; seconds: number; };
    ringIndex: number;
    isEditing: boolean;
    id?: any;
  }
  
  @Component
  export default class SexagesimalFormatTable extends Vue {
    coordinatesList: CoordinateRow[] = [];
  
    headers = [
      { text: "Latitudes", value: "latitude", sortable: false },
      { text: "Longitudes", value: "longitude", sortable: false },
      { text: "Acciones", value: "actions", sortable: false, width: "100px" },
    ];
  
    get isEditingRow(): boolean {
      return this.coordinatesList.some(item => item.isEditing);
    }
  
    get saveStatusMessage(): string {
      if (this.coordinatesList.length < 3) return validationMessages.NOT_ENOUGH_POINTS;
      if (this.isEditingRow) return validationMessages.POINT_WITHOUT_SAVE;
      return "";
    }
  
    addRow() {
      this.coordinatesList.push({
        latitude: { direction: "N", degrees: 0, minutes: 0, seconds: 0 },
        longitude: { direction: "W", degrees: 0, minutes: 0, seconds: 0 },
        ringIndex: 0,
        isEditing: true,
      });
    }
  
    editRow(index: number) {
      this.coordinatesList[index].isEditing = true;
    }
  
    cancelEdit(index: number, row: CoordinateRow) {
      if (!row.id) {
        this.deleteRow(row);
      } else {
        this.coordinatesList[index].isEditing = false;
      }
    }
  
    deleteRow(row: CoordinateRow) {
      const index = this.coordinatesList.indexOf(row);
      if (index !== -1) this.coordinatesList.splice(index, 1);
    }

    canAddPoint(item):boolean{
        return validateCoordinate(item.latitude.degrees, 0, 90) &&
              validateCoordinate(item.latitude.minutes, 0, 59) &&
              validateCoordinate(item.latitude.seconds, 0, 59) &&
              validateCoordinate(item.longitude.degrees, 0, 180) &&
              validateCoordinate(item.longitude.minutes, 0, 59) &&
              validateCoordinate(item.longitude.seconds, 0, 59);
    }
  
    saveRow(index: number) {
      const row = this.coordinatesList[index];
      if (this.canAddPoint(row) === false) {
        this.saveStatusChanged(validationMessages.NOT_VALID_LAT_LONG);
        EventBus.$emit(TypeEventBus.SHOW_MESSAGE, validationMessages.NOT_VALID_LAT_LONG, 'error');
        return;
      }
  
      row.id = `${row.ringIndex}-${index + 1}`;
      row.isEditing = false;
    }
  
    isValidRow(row: CoordinateRow): boolean {
      return validateCoordinate(row.latitude.degrees, 0, 90) &&
             validateCoordinate(row.latitude.minutes, 0, 59) &&
             validateCoordinate(row.latitude.seconds, 0, 59) &&
             validateCoordinate(row.longitude.degrees, 0, 180) &&
             validateCoordinate(row.longitude.minutes, 0, 59) &&
             validateCoordinate(row.longitude.seconds, 0, 59);
    }
  
    getDecimalValuesFromTable(): google.maps.LatLngLiteral[][] {
      const ringsMap = new Map<number, google.maps.LatLngLiteral[]>();
      this.coordinatesList.forEach(row => {
        const lat = convertToDecimal(row.latitude.direction, row.latitude.degrees, row.latitude.minutes, row.latitude.seconds);
        const lng = convertToDecimal(row.longitude.direction, row.longitude.degrees, row.longitude.minutes, row.longitude.seconds);
        if (!ringsMap.has(row.ringIndex)) ringsMap.set(row.ringIndex, []);
        ringsMap.get(row.ringIndex)!.push({ lat, lng });
      });

      let data = Array.from(ringsMap.values());
      return data
    }
  
    exportToGeoJSON(): FeatureCollection | null {
      const validRingsMap = new Map<number, google.maps.LatLngLiteral[]>();
      this.coordinatesList.forEach(row => {
        if (!this.isValidRow(row)) return;
        const lat = convertToDecimal(row.latitude.direction, row.latitude.degrees, row.latitude.minutes, row.latitude.seconds);
        const lng = convertToDecimal(row.longitude.direction, row.longitude.degrees, row.longitude.minutes, row.longitude.seconds);
        if (!validRingsMap.has(row.ringIndex)) validRingsMap.set(row.ringIndex, []);
        validRingsMap.get(row.ringIndex)!.push({ lat, lng });
      });

      const rings = Array.from(validRingsMap.values()).map(ringCoords => {
        const normalized = normalizePolygonCoords(ringCoords);
        return normalized.map(c => [c.lng, c.lat]);
      });

      if (rings.length < 1 || rings[0].length < 3) {
        alert("Se necesitan al menos 3 puntos válidos para formar un polígono.");
        return null;
      }

      return {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "MultiPolygon",
              coordinates: [rings]
            },
            properties: {}
          }
        ]
      };
    }
  
    updateCoordinatesList(sexagesimalCoords: CoordinateRow[]) {
      this.coordinatesList = sexagesimalCoords.map(coord => ({
        latitude: { ...coord.latitude },
        longitude: { ...coord.longitude },
        ringIndex: coord.ringIndex || 0,
        isEditing: false,
        id: coord.id || null
      }));
    }
  
    cancelChanges() {
      this.coordinatesList.forEach((row, index) => {
        if (row.isEditing) this.cancelEdit(index, row);
      });
    }
  
    clickSavePolygon() {
      const geoJson = this.exportToGeoJSON();
      if (geoJson) this.updatePolygon(geoJson);
    }
  
    clickPreviewPolygonsInMap() {
      const geoJson = this.exportToGeoJSON();
      if (geoJson) this.geojsonToPreview(geoJson);
    }
  
    @Watch("saveStatusMessage", { immediate: true })
    onSaveStatusMessageChanged(newMessage: string) {
      this.saveStatusChanged(newMessage);
    }
  
    @Emit()
    saveStatusChanged(message: string) {}
  
    @Emit()
    geojsonToPreview(geoJson: FeatureCollection) {}
  
    @Emit()
    updatePolygon(geoJson: FeatureCollection) {}
  }
  